import ParameterValidator from '@mvps-genesis/parameter-validator';
import {MenuBar, MenuItem} from '@mvps-genesis/accessibility';
import {getAbsoluteParent, getNextSibling, getParent} from '@mvps-genesis/selector-helpers';
import {createFocusTrap} from 'focus-trap';

/* eslint-disable no-invalid-this, max-lines-per-function */
export default class NavigationMenu {

	/**
	 * Options:
	 *
	 * options.drawers
	 * options.mainMenu
	 * options.mainMenuOpenButtons
	 * options.mainMenuCloseButtons
	 * options.menuItems
	 * options.parentMenus
	 * options.showPreviousMenuButtons
	 */
	constructor (options) {

		// Create validator
		this._validator = new ParameterValidator(this);

		this.options = this._validator.isObject('options', options);
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '75em');

		this.elements = {
			mainMenu: this._validator.isElement('options.mainMenu', options.mainMenu),
			mainMenuCloseButtons: this._validator.isElementList('options.mainMenuCloseButtons', options.mainMenuCloseButtons),
			mainMenuOpenButtons: this._validator.isElementList('options.mainMenuOpenButtons', options.mainMenuOpenButtons),
			parentMenus: this._validator.isElementList('options.parentMenus', options.parentMenus),
			showPreviousMenuButtons: this._validator.isElementList('options.showPreviousMenuButtons', options.showPreviousMenuButtons)
		};
		this.elements.menuItems = this._validator.isElementList('options.menuItems', options.menuItems, {container: this.elements.mainMenu});
		this.elements.parentMenuItems = this._validator.isElementList('parentMenuItems', `${options.parentMenus} > ${options.menuItems}`, {container: this.elements.mainMenu});
		this.elements.expandButtons = this._validator.isElementList('options.expandButtons', options.expandButtons, {container: this.elements.mainMenu});
		this.elements.drawers = this._validator.isElementList('options.drawers', options.drawers, {container: this.elements.mainMenu});

		this.accessibility = {
			// Create focus trap for mobile menu
			focusTrap: createFocusTrap(this.elements.mainMenu, {
				escapeDeactivates: false,
				initialFocus: () => {
					if (this.isMobile()) {
						return this.elements.mainMenu.querySelector('.js-navigation-menu__has-child > .js-navigation-menu__link');
					}

					return false;
				},
				setReturnFocus: this.elements.mainMenuOpenButtons[0]
			}),
			// Create accessible (keyboard navigation) menu from the main NavigationMenu element
			menuBar: new MenuBar({element: this.elements.mainMenu})
		};

		this.bindEvents();
		this.updateDrawerFocusability();
	}

	isMobile () {
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	bindEvents (add = true) {
		const method = add ? 'addEventListener' : 'removeEventListener';

		if (add) {
			this.elements.mainMenu.addEventListener('focusin', this.handleDrawerAutoClose, false);
			this.elements.mainMenu.addEventListener('keydown', this.handleEscapeKey, false);
		} else {
			this.elements.mainMenu.removeEventListener('focusin', this.handleDrawerAutoClose, false);
			this.elements.mainMenu.removeEventListener('keydown', this.handleEscapeKey, false);
		}

		window[method]('resize', this.updateDrawerFocusability, false);

		// Add listener for top-level close
		this.accessibility.menuBar[method](MenuBar.events.close, (e) => this.onAccessibilityMenuClose(e), true);

		// Mobile open and close events
		for (const mainMenuOpenButton of this.elements.mainMenuOpenButtons) {
			mainMenuOpenButton[method]('click', this.mainMenuOpen, false);
		}

		for (const mainMenuCloseButton of this.elements.mainMenuCloseButtons) {
			mainMenuCloseButton[method]('click', this.mainMenuClose, false);
		}

		for (const menuItem of this.elements.menuItems) {
			menuItem[method]('keydown', this.resetTabIndexes, false);
		}

		for (const parentMenuItem of this.elements.parentMenuItems) {
			parentMenuItem[method]('click', this.onParentMenuItemClick, false);
			parentMenuItem[method](MenuBar.events.enterSubMenu, this.onParentMenuItemClick, false);
		}

		for (const parentMenuItem of this.elements.parentMenuItems) {
			if (!parentMenuItem.classList.contains('g-navigation-menu__title')) {
				parentMenuItem[method]('focus', this.closeAllSubmenus, false);
			}
		}

		// Show previous button
		for (const showPreviousMenuButton of this.elements.showPreviousMenuButtons) {
			showPreviousMenuButton[method]('click', this.onPreviousButtonClick, false);
		}

		for (const expandButton of this.elements.expandButtons) {
			expandButton[method]('click', this.onExpandButtonClick, false);
			expandButton[method]('focus', this.closeAllSubmenus, false);
		}

		document[method]('click', this.onDocumentClick, false);
		document[method]('keydown', this.onDocumentKeydown, false);

		for (const drawer of this.elements.drawers) {
			drawer[method]('focusout', this.onDrawerFocusOut, false);
		}
	}

	destroyEvents () {
		this.bindEvents(false);
	}

	mainMenuOpen = () => {
		document.documentElement.classList.add('has-open-main-menu');
		this.elements.mainMenu.classList.add('is-open');
		this.elements.mainMenu.classList.remove('is-hidden');

		for (const button of this.elements.mainMenuOpenButtons) {
			button.setAttribute('aria-expanded', 'true');
		}

		if (this.isMobile()) {
			this.updateDrawerFocusability();
			this.disableMenuBarKeyboardHandling();

			setTimeout(() => {
				this.accessibility.focusTrap.activate();
			}, 300);
		} else {
			this.accessibility.menuBar.focusFirstMenuItem();
			this.accessibility.focusTrap.activate();
		}
	}

	disableMenuBarKeyboardHandling = () => {
		const menuItems = this.elements.mainMenu.querySelectorAll('[role="menuitem"]');

		menuItems.forEach(item => {
			if (!item._originalKeydownHandler) {
				item._originalKeydownHandler = item.onkeydown;
			}

			item.onkeydown = null;

			const menuItemInstance = item._getMenuItem && item._getMenuItem();

			if (menuItemInstance) {
				menuItemInstance.destroyEvents();
			}
		});
	}

	enableMenuBarKeyboardHandling = () => {
		const menuItems = this.elements.mainMenu.querySelectorAll('[role="menuitem"]');

		menuItems.forEach(item => {
			const menuItemInstance = item._getMenuItem && item._getMenuItem();

			if (menuItemInstance) {
				menuItemInstance.bindEvents();
			}
		});
	}

	mainMenuClose = () => {
		document.documentElement.classList.remove('has-open-main-menu');
		this.elements.mainMenu.classList.remove('is-open');
		this.elements.mainMenu.classList.add('is-hidden');

		for (const button of this.elements.mainMenuOpenButtons) {
			button.setAttribute('aria-expanded', 'false');
		}

		if (!this.isMobile()) {
			this.accessibility.menuBar.resetMenuItemsState();
		}

		this.accessibility.focusTrap.deactivate();

		if (this.isMobile()) {
			this.enableMenuBarKeyboardHandling();
		}
	}

	onAccessibilityMenuClose (e) {
		if (e && e.detail && e.detail.menu) {
			if (this.isMobile()) {
				if (e.detail.menu.root) {
					this.mainMenuClose();
				} else {
					const previousButton = e.detail.menu.element.querySelector(this.options.showPreviousMenuButtons);
					this.showPreviousMenu(previousButton);
				}
			} else {
				const drawer = e.detail.menu.element;
				const parentDiv = drawer?.closest(this.options.parentMenus);

				if (parentDiv) {
					parentDiv.classList.remove('is-open');

					if (drawer) {
						drawer.style.transitionDelay = '';
						drawer.style.visibility = '';
					}

					const parentLink = parentDiv.querySelector(`:scope > ${this.options.menuItems}`);

					if (parentLink) {
						parentLink.setAttribute('tabindex', '0');
						parentLink.setAttribute('aria-expanded', 'false');
					}
				}
			}
		}

		return true;
	}

	onMenuItemFocus = (e) => {
		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.add('is-open');
		}
	}

	onMenuItemBlur = (e) => {
		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.remove('is-open');
		}
	}

	onParentMenuItemClick = (e) => {
		e.stopPropagation();

		if (!this.isMobile()) {
			if (e.type === MenuBar.events.enterSubMenu) {
				e.preventDefault();

				const parent = getAbsoluteParent(e.target, this.options.parentMenus);

				if (parent) {
					this.closeAllSubmenus();
					parent.classList.add('is-open');
					e.target.setAttribute('aria-expanded', 'true');

					const drawer = parent.querySelector(this.options.drawers);

					if (drawer) {
						drawer.removeAttribute('inert');
						drawer.style.transitionDelay = '0s';
						drawer.style.visibility = 'visible';
					}
				}
			}

			return true;
		}

		e.preventDefault();

		let clickElement = e.target;
		const parentElement = getParent(clickElement, this.options.menuItems);

		if (parentElement) {
			clickElement = parentElement;
		}

		const nextDrawer = getNextSibling(clickElement, this.options.drawers);

		if (nextDrawer) {
			nextDrawer.classList.add('is-open');
			this.updateDrawerFocusability();

			setTimeout(() => {
				const firstDrawerLink = nextDrawer.querySelector('[role="menuitem"]');

				if (firstDrawerLink) {
					firstDrawerLink.focus();
				}
			}, 0);
		}

		return false;
	}

	onPreviousButtonClick = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const parentDrawer = getParent(e.target, this.options.drawers);
		parentDrawer.classList.remove('is-open');
		this.updateDrawerFocusability();

		// Properly handle correct focusing on "back"
		const menuItem = MenuItem.getInstance(e.target);

		if (menuItem) {
			menuItem.dispatchEvent('keydown', {
				key: 'Escape'
			});
		}
	}

	showPreviousMenu (target) {
		const parentDrawer = getParent(target, this.options.drawers);

		if (parentDrawer) {
			parentDrawer.classList.remove('is-open');
		}
	}

	onExpandButtonClick = (e) => {
		if (this.isMobile()) {
			let clickElement = e.target;
			const parentElement = getParent(clickElement, this.options.parentMenus);

			if (parentElement) {
				const nextDrawer = parentElement.querySelector(this.options.drawers);

				if (nextDrawer) {
					nextDrawer.classList.add('is-open');
					this.updateDrawerFocusability();
				}
			}

			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			const isOpen = parent.classList.toggle('is-open');
			e.target.setAttribute('aria-expanded', String(isOpen));
		}
	}

	onDocumentClick = (e) => {
		if (this.isMobile()) {
			return;
		}

		if (!this.elements.mainMenu.contains(e.target)) {
			this.closeAllSubmenus();
		}
	}

	onDocumentKeydown = (e) => {
		if (this.isMobile()) {
			return;
		}

		if (e.key === 'Escape') {
			this.closeAllSubmenus();
		}
	}

	onDrawerFocusOut = (e) => {
		if (this.isMobile()) {
			return;
		}

		const drawer = e.currentTarget;

		requestAnimationFrame(() => {
			if (!drawer.contains(document.activeElement)) {
				const parentDiv = drawer.closest(this.options.parentMenus);

				if (parentDiv) {
					parentDiv.classList.remove('is-open');

					const parentLink = parentDiv.querySelector(this.options.menuItems);

					if (parentLink) {
						parentLink.setAttribute('aria-expanded', 'false');
					}
				}
			}
		});
	}

	closeAllSubmenus = () => {
		for (const expandButton of this.elements.expandButtons) {
			this.closeSubmenu(expandButton);
		}

		for (const drawer of this.elements.drawers) {
			drawer.style.transitionDelay = '';
			drawer.style.visibility = '';
		}
	}

	closeSubmenu = (button) => {
		const parent = getAbsoluteParent(button, this.options.parentMenus);

		if (parent) {
			parent.classList.remove('is-open');
			button.setAttribute('aria-expanded', 'false');
		}
	}

	resetTabIndexes = (e) => {
		if (this.isMobile()) {
			return;
		}

		if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(e.key)) {
			for (const menuItem of this.elements.menuItems) {
				if (menuItem.tabIndex === -1) {
					menuItem.tabIndex = 0;
				}
			}
		}
	}

	updateDrawerFocusability = () => {
		const drawers = this.elements.drawers;
		const parentLinks = this.elements.parentMenuItems;
		const expandButtons = Array.from(this.elements.expandButtons).filter(button => !button.classList.contains('js-navigation-menu__link'));

		if (!drawers.length || !parentLinks.length || !expandButtons.length) {
			return;
		}

		if (!this.isMobile()) {
			drawers.forEach(drawer => {
				drawer.removeAttribute('inert');
			});

			parentLinks.forEach(link => {
				link.setAttribute('tabindex', '0');

				if (link.hasAttribute('data-original-aria-label')) {
					const originalLabel = link.getAttribute('data-original-aria-label');
					link.setAttribute('aria-label', originalLabel);
					link.removeAttribute('data-original-aria-label');
				}
			});

			expandButtons.forEach(button => {
				button.setAttribute('tabindex', '-1');
			});

			if (this.accessibility.focusTrap) {
				this.accessibility.focusTrap.updateContainerElements();
			}

			return;
		}

		parentLinks.forEach(link => {
			link.setAttribute('tabindex', '0');

			if (!link.classList.contains('js-navigation-menu__expand')) {
				const linkName = link.getAttribute('data-link-name');

				if (linkName) {
					const currentAriaLabel = link.getAttribute('aria-label');

					if (!link.hasAttribute('data-original-aria-label')) {
						link.setAttribute('data-original-aria-label', currentAriaLabel || '');
					}

					link.setAttribute('aria-label', `Expand ${linkName} sub-menu`);
				}
			}
		});

		expandButtons.forEach(button => {
			button.setAttribute('tabindex', '-1');
		});

		drawers.forEach(drawer => {
			if (drawer.classList.contains('is-open')) {
				drawer.removeAttribute('inert');
			} else {
				drawer.setAttribute('inert', '');
			}
		});
	}

	handleDrawerAutoClose = (e) => {
		if (!this.isMobile()) {
			return;
		}

		const openDrawers = Array.from(this.elements.drawers).filter(drawer => drawer.classList.contains('is-open'));

		if (!openDrawers.length) {
			return;
		}

		openDrawers.forEach(drawer => {
			const isFocusInDrawer = drawer.contains(e.target);

			if (!isFocusInDrawer) {
				drawer.classList.remove('is-open');
				this.updateDrawerFocusability();
			}
		});
	}

	handleEscapeKey = (e) => {
		if (e.key === 'Escape' || e.key === 'Esc') {
			if (this.isMobile()) {
				e.preventDefault();
				this.mainMenuClose();
			}
		}
	}
};
